import { createContext, ReactNode, useContext, useState } from "react";
import { notaFiscalProduto } from "../interfaces/notaFiscal/notaFiscalProduto";
import { statusOptions } from "../interfaces/statusOptions";
import { FileContext } from "./FileContextProvider";
import { ScannerContext } from "./ScannerContextProvider";
import statusGreenCircle from "../assets/images/status/statusGreenCircle.svg";
import statusRedCircle from "../assets/images/status/statusRedCircle.svg";
import statusYellowCircle from "../assets/images/status/statusYellowCircle.svg";
import statusGrayCircle from "../assets/images/status/statusGrayCircle.svg";
import { ProductStatus } from "../types/ProductStatus";
import { composeUniqueKey, isSameProduct } from "../utils/notaFiscalUtils";

interface StatusContextType {
  presentItensCounter: number;
  notFoundItensCounter: number;
  divergentItensTotalCounter: number;
  notCountedItensCounter: number;
  getStatusOptionsArray: (groupNumber?: number) => statusOptions[];
  calculateCounters: (primaryArray: notaFiscalProduto[], secondaryArray: notaFiscalProduto[], isGettingCounter: boolean) => void;
  isStatusCounterEmpty: () => boolean;
  canEditProductScanner: (productIdentifier: string) => boolean;
}

export const StatusContext = createContext<StatusContextType>({
  notCountedItensCounter: 0,
  presentItensCounter: 0,
  notFoundItensCounter: 0,
  divergentItensTotalCounter: 0,
  calculateCounters: () => {},
  getStatusOptionsArray: () => [],
  isStatusCounterEmpty: () => true,
  canEditProductScanner: () => false,
});

export function StatusContextProvider({ children }: { children: ReactNode }) {
  const { notaFiscalProductsArray } = useContext(FileContext);
  const { scannerArray } = useContext(ScannerContext);

  const [presentItensCounter, setPresentItensCounter] = useState(0);
  const [notFoundItensCounter, setNotFoundItensCounter] = useState(0);
  const [divergentItensTotalCounter, setDivergentItensTotalCounter] = useState(0);
  const [notCountedItensCounter, setNotCountedItensCounter] = useState(0);

  function calculateCounters(primaryArray: notaFiscalProduto[], secondaryArray: notaFiscalProduto[], isGettingCounter: boolean) {
    let present = 0;
    let notFound = 0;
    let divergent = 0;

    const uniqueProducts: notaFiscalProduto[] = [];

    primaryArray.forEach((item) => {
      const uniqueKey = composeUniqueKey(item);
      const existingProduct = uniqueProducts.find((prod) => composeUniqueKey(prod) === uniqueKey);
      if (existingProduct) {
        existingProduct.qCom += item.qCom;
      } else {
        uniqueProducts.push({ ...item });
      }
    });

    uniqueProducts.forEach((item) => {
      const isItemPresent = secondaryArray.some((scannerItem) => isSameProduct(scannerItem, item));

      const totalScannerQuantity = secondaryArray
        .filter((scannerItem) => isSameProduct(scannerItem, item))
        .reduce((sum, scannerItem) => sum + scannerItem.qCom, 0);

      const isTotalEqual = totalScannerQuantity === item.qCom;

      if (isItemPresent && isTotalEqual) present++;
      if (isItemPresent && !isTotalEqual) divergent++;
    });

    if (isGettingCounter) {
      const uniqueSecondaryArrayIdentifiers = new Set<string>(secondaryArray.map((item) => composeUniqueKey(item)));

      uniqueSecondaryArrayIdentifiers.forEach((itemIdentifier) => {
        const isItemPresentOnUniqueProductsArray = uniqueProducts.some(
          (product) => composeUniqueKey(product) === itemIdentifier,
        );

        if (!isItemPresentOnUniqueProductsArray) {
          notFound++;
        }
      });
    }
    const notCounted = uniqueProducts.length - (present + divergent);

    setNotCountedItensCounter(notCounted);
    setPresentItensCounter(present);
    setNotFoundItensCounter(notFound);
    setDivergentItensTotalCounter(divergent);
  }

  function getStatusOptionsArray(groupNumber?: number) {
    const presentItensCounterTitle = "Confere";
    const divergentItensTotalCounterTitle = "Não confere";
    const notCountedItensCounterTitle = "Não contado";
    const notFoundItensCounterTitle = "Inesperado";

    const completeStatusOptionsArray: statusOptions[] = [
      { image: statusGreenCircle, title: presentItensCounterTitle, totalStatus: presentItensCounter },
      { image: statusYellowCircle, title: divergentItensTotalCounterTitle, totalStatus: divergentItensTotalCounter },
      { image: statusGrayCircle, title: notCountedItensCounterTitle, totalStatus: notCountedItensCounter },
      { image: statusRedCircle, title: notFoundItensCounterTitle, totalStatus: notFoundItensCounter },
    ];

    if (groupNumber === 1) {
      const statusOptionsArray: statusOptions[] = [
        { image: statusGreenCircle, title: presentItensCounterTitle, totalStatus: presentItensCounter },
        { image: statusYellowCircle, title: divergentItensTotalCounterTitle, totalStatus: divergentItensTotalCounter },
      ];
      return statusOptionsArray;
    }
    if (groupNumber === 2) {
      const statusOptionsArray: statusOptions[] = [
        { image: statusGrayCircle, title: notCountedItensCounterTitle, totalStatus: notCountedItensCounter },
        { image: statusRedCircle, title: notFoundItensCounterTitle, totalStatus: notFoundItensCounter },
      ];
      return statusOptionsArray;
    }
    return completeStatusOptionsArray;
  }

  function isStatusCounterEmpty() {
    if (notaFiscalProductsArray.length > 0 && scannerArray.length > 0) {
      return false;
    }
    return true;
  }
  function getProductStatusScanner(productIdentifier: string): ProductStatus {
    const nota = notaFiscalProductsArray.find((item) => composeUniqueKey(item) === productIdentifier);

    if (!nota) return ProductStatus.NotFound;

    const totalScanner = scannerArray
      .filter((item) => composeUniqueKey(item) === productIdentifier)
      .reduce((sum, item) => sum + item.qCom, 0);

    if (totalScanner === 0) return ProductStatus.NotCounted;
    if (totalScanner === nota.qCom) return ProductStatus.Present;

    return ProductStatus.Divergent;
  }

  function canEditProductScanner(productIdentifier: string): boolean {
    return getProductStatusScanner(productIdentifier) === ProductStatus.NotFound;
  }
  const values: StatusContextType = {
    calculateCounters: calculateCounters,
    divergentItensTotalCounter: divergentItensTotalCounter,
    notCountedItensCounter: notCountedItensCounter,
    presentItensCounter: presentItensCounter,
    notFoundItensCounter: notFoundItensCounter,
    getStatusOptionsArray: getStatusOptionsArray,
    isStatusCounterEmpty: isStatusCounterEmpty,
    canEditProductScanner,
  };

  return <StatusContext.Provider value={values}>{children}</StatusContext.Provider>;
}
